<template>
    <v-card class="card-form-outlined">
        <v-card-title class="academic-panel-title">
            <div>
                <span>Componentes e docentes</span>
                <small>Defina primeiro o que a turma estuda e quem ministra cada componente.</small>
            </div>
        </v-card-title>

        <v-card-text>
            <v-row align="center" class="academic-add-row">
                <v-col cols="12" md="5">
                    <v-select
                        dense
                        label="Componente curricular"
                        :items="availableCourses"
                        item-text="name"
                        item-value="id"
                        v-model="form.course_id"
                        outlined
                        hide-details="auto"
                    />
                </v-col>
                <v-col cols="12" sm="6" md="2">
                    <v-text-field
                        dense
                        type="number"
                        min="1"
                        label="Aulas por semana"
                        v-model="form.weekly_workload"
                        outlined
                        hide-details="auto"
                    />
                </v-col>
                <v-col cols="12" sm="6" md="2">
                    <v-text-field
                        dense
                        type="number"
                        min="1"
                        label="Carga anual"
                        v-model="form.annual_workload"
                        outlined
                        hide-details="auto"
                    />
                </v-col>
                <v-col cols="12" md="3">
                    <v-btn
                        block
                        color="primary"
                        elevation="0"
                        :loading="loading"
                        :disabled="!form.course_id"
                        @click="createOffering"
                    >
                        <v-icon left small>mdi-plus</v-icon>
                        Adicionar componente
                    </v-btn>
                </v-col>
            </v-row>

            <div v-if="offerings.length" class="academic-offerings">
                <section v-for="offering in offerings" :key="offering.id" class="academic-offering">
                    <div class="academic-offering-heading">
                        <div class="academic-offering-icon">
                            <v-icon>mdi-book-open-page-variant-outline</v-icon>
                        </div>
                        <div>
                            <strong>{{ offering.course ? offering.course.name : "Componente" }}</strong>
                            <small>{{ workloadLabel(offering) }}</small>
                        </div>
                        <v-spacer />
                        <v-btn icon small title="Remover componente" @click="$emit('delete-offering', offering)">
                            <v-icon small>mdi-delete-outline</v-icon>
                        </v-btn>
                    </div>

                    <div class="academic-offering-settings">
                        <v-text-field
                            dense
                            type="number"
                            min="1"
                            label="Aulas por semana"
                            v-model="offeringDraft(offering).weekly_workload"
                            outlined
                            hide-details="auto"
                        />
                        <v-text-field
                            dense
                            type="number"
                            min="1"
                            label="Carga anual"
                            v-model="offeringDraft(offering).annual_workload"
                            outlined
                            hide-details="auto"
                        />
                        <v-btn
                            color="primary"
                            outlined
                            elevation="0"
                            :loading="loading"
                            @click="updateOffering(offering)"
                        >
                            Atualizar carga
                        </v-btn>
                    </div>

                    <div class="academic-teachers">
                        <div class="academic-teacher-list">
                            <span class="academic-field-label">Docentes vinculados</span>
                            <div v-if="offering.teachers && offering.teachers.length" class="academic-chip-list">
                                <v-chip
                                    v-for="assignment in offering.teachers"
                                    :key="assignment.id"
                                    label
                                    small
                                    close
                                    @click:close="$emit('delete-teacher', { offering, assignment })"
                                >
                                    <v-icon left small>mdi-account-tie-outline</v-icon>
                                    {{ assignment.employee ? assignment.employee.name : "Professor" }}
                                    <span v-if="assignment.is_primary">&nbsp;· Titular</span>
                                </v-chip>
                            </div>
                            <span v-else class="academic-empty">Nenhum docente vinculado.</span>
                        </div>

                        <div class="academic-teacher-form">
                            <v-select
                                dense
                                label="Adicionar professor"
                                :items="teachersFor(offering)"
                                item-text="name"
                                item-value="id"
                                v-model="teacherDraft(offering.id).employee_id"
                                outlined
                                hide-details="auto"
                            />
                            <v-checkbox
                                dense
                                label="Titular"
                                v-model="teacherDraft(offering.id).is_primary"
                                hide-details
                            />
                            <v-btn
                                color="primary"
                                outlined
                                elevation="0"
                                :disabled="!teacherDraft(offering.id).employee_id"
                                @click="addTeacher(offering)"
                            >
                                Vincular
                            </v-btn>
                        </div>
                    </div>
                </section>
            </div>

            <div v-else class="academic-empty-state">
                <v-icon>mdi-book-plus-outline</v-icon>
                <strong>Nenhum componente configurado</strong>
                <span>Adicione os componentes antes de montar a grade semanal.</span>
            </div>
        </v-card-text>
    </v-card>
</template>

<script>
export default {
    name: "ClassComponentsPanel",

    props: {
        offerings: { type: Array, default: () => [] },
        courses: { type: Array, default: () => [] },
        teachers: { type: Array, default: () => [] },
        loading: { type: Boolean, default: false },
    },

    data: () => ({
        form: {
            course_id: null,
            weekly_workload: null,
            annual_workload: null,
        },
        teacherDrafts: {},
        offeringDrafts: {},
    }),

    computed: {
        availableCourses() {
            const used = new Set(this.offerings.map((item) => Number(item.course_id)));
            return this.courses.filter((course) => !used.has(Number(course.id)));
        },
    },

    methods: {
        teacherDraft(id) {
            if (!this.teacherDrafts[id]) {
                this.$set(this.teacherDrafts, id, {
                    employee_id: null,
                    is_primary: false,
                });
            }
            return this.teacherDrafts[id];
        },

        offeringDraft(offering) {
            if (!this.offeringDrafts[offering.id]) {
                this.$set(this.offeringDrafts, offering.id, {
                    weekly_workload: offering.weekly_workload || null,
                    annual_workload: offering.annual_workload || null,
                });
            }
            return this.offeringDrafts[offering.id];
        },

        teachersFor(offering) {
            const used = new Set(
                (offering.teachers || []).map((assignment) => Number(assignment.employee_id))
            );
            return this.teachers.filter((teacher) => !used.has(Number(teacher.id)));
        },

        workloadLabel(offering) {
            const labels = [];
            if (offering.weekly_workload) labels.push(`${offering.weekly_workload} aula(s) por semana`);
            if (offering.annual_workload) labels.push(`${offering.annual_workload} aula(s) no ano`);
            return labels.length ? labels.join(" · ") : "Carga horária ainda não informada";
        },

        createOffering() {
            this.$emit("create-offering", { ...this.form });
            this.form = { course_id: null, weekly_workload: null, annual_workload: null };
        },

        addTeacher(offering) {
            const draft = { ...this.teacherDraft(offering.id) };
            this.$emit("add-teacher", { offering, teacher: draft });
            this.$set(this.teacherDrafts, offering.id, {
                employee_id: null,
                is_primary: false,
            });
        },

        updateOffering(offering) {
            this.$emit("update-offering", {
                offering,
                data: { ...this.offeringDraft(offering) },
            });
        },
    },
};
</script>

<style scoped lang="scss">
.academic-panel-title {
    align-items: flex-start;
    display: flex;
}

.academic-panel-title span,
.academic-panel-title small {
    display: block;
}

.academic-panel-title small {
    color: #64748b;
    font-size: 12px;
    font-weight: 500;
    margin-top: 3px;
}

.academic-add-row {
    border-bottom: 1px solid #e5eaf2;
    padding-bottom: 14px;
}

.academic-offerings {
    display: grid;
    gap: 12px;
    margin-top: 18px;
}

.academic-offering {
    border: 1px solid #dfe6ef;
    border-radius: 8px;
    padding: 14px;
}

.academic-offering-heading {
    align-items: center;
    display: flex;
    gap: 10px;
}

.academic-offering-heading strong,
.academic-offering-heading small {
    display: block;
}

.academic-offering-heading small,
.academic-empty,
.academic-field-label {
    color: #64748b;
    font-size: 12px;
}

.academic-offering-icon {
    align-items: center;
    background: #edf4ff;
    border-radius: 6px;
    display: flex;
    height: 38px;
    justify-content: center;
    width: 38px;
}

.academic-teachers {
    align-items: end;
    display: grid;
    gap: 16px;
    grid-template-columns: minmax(240px, 1fr) minmax(440px, 1.25fr);
    margin-top: 14px;
}

.academic-offering-settings {
    align-items: center;
    display: grid;
    gap: 10px;
    grid-template-columns: minmax(160px, 220px) minmax(160px, 220px) auto;
    margin-top: 14px;
}

.academic-chip-list {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 7px;
}

.academic-teacher-form {
    align-items: center;
    display: grid;
    gap: 10px;
    grid-template-columns: minmax(200px, 1fr) auto auto;
}

.academic-empty-state {
    align-items: center;
    color: #64748b;
    display: flex;
    flex-direction: column;
    gap: 5px;
    justify-content: center;
    min-height: 130px;
}

.academic-empty-state .v-icon {
    font-size: 32px;
}

@media only screen and (max-width: 960px) {
    .academic-teachers,
    .academic-teacher-form,
    .academic-offering-settings {
        grid-template-columns: 1fr;
    }
}
</style>
